import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { adminApi } from '../../api/admin.api';

type PeriodType = 'DAILY' | 'WEEKLY' | 'MONTHLY';

/**
 * 설문 생성 트렌드 막대 차트
 */
export const SurveyTrendChart: React.FC = () => {
  const [period, setPeriod] = useState<PeriodType>('DAILY');

  const { data: graphData, isLoading } = useQuery({
    queryKey: ['graphData'],
    queryFn: async () => {
      const response = await adminApi.getGraphData();
      return response.data;
    },
  });

  const trendData = useMemo(() => {
    if (!graphData) return undefined;
    const periodKey = period.toLowerCase() as 'daily' | 'weekly' | 'monthly';
    return graphData.surveyTrend[periodKey];
  }, [graphData, period]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 border border-[#E5E7EB]">
        <h3 className="text-lg font-semibold text-[#030213] mb-4">설문 생성 트렌드</h3>
        <div className="h-[300px] flex items-center justify-center">
          <p className="text-[#6B7280]">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#030213]">설문 생성 트렌드</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setPeriod('DAILY')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              period === 'DAILY'
                ? 'bg-[#3B82F6] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            일별
          </button>
          <button
            onClick={() => setPeriod('WEEKLY')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              period === 'WEEKLY'
                ? 'bg-[#3B82F6] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            주별
          </button>
          <button
            onClick={() => setPeriod('MONTHLY')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              period === 'MONTHLY'
                ? 'bg-[#3B82F6] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            월별
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6 border border-[#E5E7EB]">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={trendData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="month"
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
              width={30}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
            />
            <Bar
              dataKey="surveyCount"
              fill="#3B82F6"
              radius={[8, 8, 0, 0]}
              name="설문 수"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
